/**
 * Private utilities for the Ajax implementation.
 *
 *
 * @hidden
 * @packageDocumentation
 */
import type { AjaxResponse, FailureResponse } from './Ajax';
import type { AxiosResponse } from 'axios';
import axios, { AxiosError } from 'axios';
import { ErrorType } from './Error';
import _ from 'lodash';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
declare const Liferay: any;

const extendThrottled = _.throttle(() => Liferay?.Session?.extend(), 5000, {
  leading: true
});
export async function extendSession(): Promise<void> {
  extendThrottled();
}

/**
 * Process successful asynchronous call.
 *
 * @param response data returned by requested endpoint
 * @param isArray whether response is array type
 */
export function handleResponse<T>(
  response: AxiosResponse,
  isArray: boolean
): AjaxResponse<T> {
  const origin = response.request.responseURL;
  // check liferay session
  if (Liferay && !Liferay.Session) {
    console.warn('Liferay.Session is not defined yet. Ignore it for now.');
  } else {
    // handle session time out
    const sessionState = Liferay?.Session?.get('sessionState');
    if (sessionState !== 'active' && sessionState !== 'warned') {
      console.warn(
        'Session expired. Lets handle it the Liferay way. Here is the original message: ',
        response
      );
      Liferay.Session.expire();
      return {
        success: false,
        error: {
          type: ErrorType.CODED,
          code: 'UTIL00010',
          msg: 'Session is expired. Please login again!',
          title: 'Session Timout',
          uniqueCorrelationId: undefined, // Not a real exception
          origin
        }
      };
    }
  }
  // if redirect to login page was triggered put a warning on the screen
  // we can't check for HTTP-Status 302 since per specification this redirect is hidden
  const originURl = new URL(origin);
  if (
    normalizeUrl(response.config.url) !== normalizeUrl(origin) &&
    originURl.pathname.includes('login')
  ) {
    const path = window.location.pathname;
    const redirectUrl =
      origin.slice(0, origin.lastIndexOf('redirect')) + 'redirect=';
    const newUrl = redirectUrl + encodeURI(path);
    // replace deletes the session history, if this is not wanted we should switch to assign
    // https://developer.mozilla.org/en-US/docs/Web/API/Location/replace
    window.location.replace(newUrl);
  }
  const body = response.data;
  // error handling
  if (!body.success) {
    const failure = {
      success: false,
      error: {
        type: ErrorType.CODED,
        code: body.errorCode,
        msg: body.message,
        title: 'ErrorCodedException',
        uniqueCorrelationId: body.uniqueCorrelationId,
        origin
      }
    };
    if (['UTIL00010', 'UTIL00001'].includes(body.errorCode)) {
      failure.error.msg = 'Session is expired. Please login again!';
      failure.error.title = 'Session Timeout';
    }
    return failure as FailureResponse;
  }
  if (body.extendSession) {
    extendSession();
  }
  return {
    success: true,
    data: isArray ? body.data : body.data[0],
    dataLength: body.dataLength
  };
}

/**
 * Normalize URL so that it could be compared with an equal URL.
 * @param url URL to normalize
 */
function normalizeUrl(url?: string): string {
  if (!url) {
    return '';
  }
  let protocol = '';
  let beforePath = url;
  let pathWithQuery = '';

  const indexProtocol = url.indexOf('://');
  if (indexProtocol !== -1) {
    protocol = url.substring(0, indexProtocol + 3);
    beforePath = url.substring(indexProtocol + 3);
  }

  const indexFirstSlash = beforePath.indexOf('/');
  if (indexFirstSlash !== -1) {
    pathWithQuery = beforePath.substring(indexFirstSlash);
    beforePath = beforePath.substring(0, indexFirstSlash);
  }

  // get rid of standard ports:
  beforePath = beforePath.replace(':443', '');
  beforePath = beforePath.replace(':80', '');

  return protocol + beforePath + pathWithQuery;
}

/**
 * Process failed asynchronous call.
 *
 * @param error data that contains failure details
 */
export function handleFailure(error: AxiosError): FailureResponse {
  console.groupCollapsed('Axios Error');
  console.trace('Failure code: %s', error.code);
  console.trace('The complete error: %O', error);
  console.groupEnd();
  if (axios.isCancel(error)) {
    return {
      success: false,
      error: {
        type: ErrorType.HTTP_CANCELLED,
        msg: error.message
      }
    };
  }
  // https://github.com/axios/axios/blob/v1.x/index.d.cts#L103
  if (error.code === AxiosError.ERR_NETWORK) {
    return {
      success: false,
      error: {
        type: ErrorType.HTTP,
        code: 'networkerr',
        title: 'Network Error',
        msg: 'No internet connection or Server not reachable'
      }
    };
  }
  if (error.response !== undefined) {
    const title = [
      error.response.status >= 500 ? 'Server Error' : 'Client Error',
      error.response.status
    ].join(': ');
    return {
      success: false,
      error: {
        type: ErrorType.HTTP,
        code: `HTTP${error.response.status}`,
        title,
        msg: error.response.statusText,
        origin: error.response.request.responseURL
      }
    };
  }
  return {
    success: false,
    error: {
      type: ErrorType.HTTP,
      code: 'HTTP0',
      title: 'Request Aborted',
      msg: 'Request Aborted'
    }
  };
}
